import { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

import { industries } from '@/data/industries';
import { industryContent } from '@/data/industryContent';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Factory, Package, FlaskConical, CheckCircle2, ArrowRight, Cog, Settings, Cpu, Leaf, Droplet, Beaker, Wrench, ShieldCheck, FileText } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ProductCarousel } from '@/components/ProductCarousel';
import SupplierLogo from '@/components/SupplierLogo';
import { getPartnersForIndustry } from '@/data/partners';
import bakeryProcessFlow from '@/assets/bakery-process-flow.png';
import bakeryProductsShowcase from '@/assets/industries/bakery-products-showcase.jpg';
import dairyHeroBg from '@/assets/industries/dairy-hero-bg.jpg';
import beverageHeroBg from '@/assets/industries/beverage-hero-bg.jpg';
import oilsFatsHeroBg from '@/assets/industries/oils-fats-hero-bg.jpg';
import meatFishEggsHeroBg from '@/assets/industries/meat-fish-eggs-hero-bg.jpg';
import spreadsSaucesHeroBg from '@/assets/industries/spreads-sauces-hero-bg.jpg';
import fruitsVegetablesHeroBg from '@/assets/industries/fruits-vegetables.jpg';
import readyMealsHeroBg from '@/assets/industries/ready-meals.jpg';
import milkImage from '@/assets/dairy-products/milk-cartons.jpg';
import yoghurtImage from '@/assets/dairy-products/yoghurt.jpg';
import cheeseImage from '@/assets/dairy-products/cheese.jpg';
import butterImage from '@/assets/dairy-products/butter.jpg';
import iceCreamImage from '@/assets/dairy-products/ice-cream.jpg';
import waterImage from '@/assets/beverages/water.jpg';
import softDrinksImage from '@/assets/beverages/soft-drinks.jpg';
import juicesImage from '@/assets/beverages/juices.jpg';
import beerImage from '@/assets/beverages/beer.jpg';
import wineImage from '@/assets/beverages/wine.jpg';
import meatFishImage from '@/assets/meat-fish/meat-fish.jpg';
import eggsImage from '@/assets/meat-fish/eggs.jpg';
import oilsImage from '@/assets/oils-fats/oils.jpg';
import fatsImage from '@/assets/oils-fats/fats.jpg';

const dairyProductImages: Record<string, string> = {
  'milk': milkImage,
  'yoghurt': yoghurtImage,
  'cheese': cheeseImage,
  'butter': butterImage,
  'ice-cream': iceCreamImage
};

const beverageProductImages: Record<string, string> = {
  'water': waterImage,
  'soft-drinks': softDrinksImage,
  'juices': juicesImage,
  'beer': beerImage,
  'wine': wineImage
};

const meatFishProductImages: Record<string, string> = {
  'meat-fish': meatFishImage,
  'eggs': eggsImage
};

const oilsFatsProductImages: Record<string, string> = {
  'oils': oilsImage,
  'fats': fatsImage
};

const IndustryDetail = () => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const industry = industries.find(ind => ind.slug === slug);

  if (!industry) {
    return <Navigate to="/industries" replace />;
  }

  const content = industryContent[slug || ''];
  const [activeProcessTab, setActiveProcessTab] = useState(0);
  const [activeMainTab, setActiveMainTab] = useState(0);
  const processIcons = [Droplet, Factory, Cpu, Leaf, Cog, Settings, Wrench, ShieldCheck];
  const industryPartners = getPartnersForIndustry(slug || '');

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={industry.title[language]}
        description={industry.description[language]}
        canonical={`https://www.tft.gr/industries/${slug}`}
      />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-b from-primary to-primary-light text-primary-foreground overflow-hidden">
          {slug === 'dairy' && (
            <div
              className="absolute inset-0 bg-cover bg-center opacity-35 pointer-events-none [filter:grayscale(1)_contrast(1.1)] mix-blend-soft-light"
              style={{ backgroundImage: `url(${dairyHeroBg})` }}
              aria-hidden="true"
            />
          )}
          {slug === 'beverage' && (
            <div
              className="absolute inset-0 opacity-35 pointer-events-none [filter:grayscale(1)_contrast(1.1)] mix-blend-soft-light bg-no-repeat bg-center bg-cover"
              style={{ backgroundImage: `url(${beverageHeroBg})` }}
              aria-hidden="true"
            />
          )}
          {slug === 'bakery-grains-snacks' && (
            <div
              className="absolute inset-0 opacity-35 pointer-events-none [filter:grayscale(1)_contrast(1.1)] mix-blend-soft-light bg-no-repeat bg-center bg-cover"
              style={{ backgroundImage: `url(${bakeryProductsShowcase})` }}
              aria-hidden="true"
            />
          )}
          {slug === 'oils-fats' && (
            <div
              className="absolute inset-0 opacity-35 pointer-events-none [filter:grayscale(1)_contrast(1.1)] mix-blend-soft-light bg-no-repeat"
              style={{ backgroundImage: `url(${oilsFatsHeroBg})`, backgroundSize: '100% auto', backgroundPosition: 'center 75%' }}
              aria-hidden="true"
            />
          )}
          {slug === 'meat-fish-eggs' && (
            <div
              className="absolute inset-0 opacity-35 pointer-events-none [filter:grayscale(1)_contrast(1.1)] mix-blend-soft-light bg-no-repeat bg-center bg-cover"
              style={{ backgroundImage: `url(${meatFishEggsHeroBg})` }}
              aria-hidden="true"
            />
          )}
          {slug === 'spreads-sauces' && (
            <div
              className="absolute inset-0 opacity-35 pointer-events-none [filter:grayscale(1)_contrast(1.1)] mix-blend-soft-light bg-no-repeat bg-center bg-cover"
              style={{ backgroundImage: `url(${spreadsSaucesHeroBg})` }}
              aria-hidden="true"
            />
          )}
          {slug === 'fruit-vegetables' && (
            <div
              className="absolute inset-0 opacity-35 pointer-events-none [filter:grayscale(1)_contrast(1.1)] mix-blend-soft-light bg-no-repeat bg-center bg-cover"
              style={{ backgroundImage: `url(${fruitsVegetablesHeroBg})` }}
              aria-hidden="true"
            />
          )}
          {slug === 'ready-meals' && (
            <div
              className="absolute inset-0 opacity-35 pointer-events-none [filter:grayscale(1)_contrast(1.1)] mix-blend-soft-light bg-no-repeat bg-center bg-cover"
              style={{ backgroundImage: `url(${readyMealsHeroBg})` }}
              aria-hidden="true"
            />
          )}
          <div className="relative max-w-5xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold mb-4">{industry.title[language]}</h1>
            <p className="text-lg max-w-3xl mx-auto opacity-90">
              {industry.description[language]}
            </p>
          </div>
        </section>

        {/* Overview */}
        {content?.overview && (
          <section className="py-10 bg-background">
            <div className="max-w-5xl mx-auto px-4">
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-3">{t('Επισκόπηση', 'Overview')}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {typeof content.overview === 'string' ? content.overview : content.overview[language]}
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* Bakery products showcase - after overview */}
        {slug === 'bakery-grains-snacks' && (
          <section className="py-10 bg-muted">
            <div className="max-w-5xl mx-auto px-4">
              <h2 className="text-2xl font-bold mb-6 text-center">{t('Προϊόντα', 'Products')}</h2>
              <div className="group relative rounded-lg overflow-hidden border border-border bg-white">
                <img
                  src={bakeryProductsShowcase}
                  alt={t('Προϊόντα αρτοποιίας και δημητριακών', 'Bakery and grain-based products')}
                  className="w-full max-h-[320px] object-cover transition-all duration-700 ease-out [filter:grayscale(1)_contrast(1.05)] group-hover:[filter:grayscale(0)_contrast(1)]"
                />
                <div
                  className="pointer-events-none absolute inset-0 bg-primary/40 mix-blend-multiply transition-opacity duration-700 ease-out group-hover:opacity-0"
                  aria-hidden="true"
                />
              </div>
            </div>
          </section>
        )}

        {/* Subcategories */}
        {industry.subcategories?.length > 0 && (
          <>
            {industry.subcategories.length > 3 ? (
              <section className="py-10 bg-muted">
                <div className="max-w-5xl mx-auto px-4">
                  <h2 className="text-2xl font-bold mb-6 text-center">{t('Προϊόντα', 'Products')}</h2>
                  <ProductCarousel 
                    subcategories={industry.subcategories}
                    slug={slug || ''}
                    language={language}
                    t={t}
                    getProductImage={(subSlug: string) => {
                      return slug === 'dairy' 
                        ? dairyProductImages[subSlug] 
                        : slug === 'beverage' 
                        ? beverageProductImages[subSlug]
                        : slug === 'meat-fish-eggs'
                        ? meatFishProductImages[subSlug]
                        : slug === 'oils-fats'
                        ? oilsFatsProductImages[subSlug]
                        : undefined;
                    }}
                  />
                </div>
              </section>
            ) : (
              <section className="py-10 bg-muted">
                <div className="max-w-5xl mx-auto px-4">
                  <h2 className="text-2xl font-bold mb-6 text-center">{t('Προϊόντα', 'Products')}</h2>
                  <div className={`grid grid-cols-1 md:grid-cols-2 ${industry.subcategories.length > 2 ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-6 ${industry.subcategories.length <= 2 ? 'lg:justify-center' : ''}`}>
                    {industry.subcategories.map((sub, index) => {
                      const productImage = slug === 'dairy' 
                        ? dairyProductImages[sub.slug] 
                        : slug === 'beverage' 
                        ? beverageProductImages[sub.slug]
                        : slug === 'meat-fish-eggs'
                        ? meatFishProductImages[sub.slug]
                        : slug === 'oils-fats'
                        ? oilsFatsProductImages[sub.slug]
                        : undefined;
                      return (
                        <Card key={index} className="hover:shadow-2xl transition-all duration-300 group border-l-4 border-l-primary overflow-hidden h-full">
                          <CardContent className="p-0 h-full">
                            <Link to={`/industries/${slug}/${sub.slug}`} className="block h-full">
                              <div className="flex flex-col h-full">
                                {productImage && (
                                  <div className="w-full h-40 relative overflow-hidden">
                                    <img
                                      src={productImage}
                                      alt={sub.title[language]}
                                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-500"
                                    />
                                  </div>
                                )}
                                <div className="flex-1 p-5 flex flex-col">
                                  <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors flex-grow">
                                    {sub.title[language]}
                                  </h3>
                                  <Button variant="ghost" className="group/btn p-0 h-auto hover:bg-transparent justify-start text-sm">
                                    <span className="mr-2">{t('Περισσότερα', 'Learn More')}</span>
                                    <ArrowRight className="h-3 w-3 group-hover/btn:translate-x-1 transition-transform" />
                                  </Button>
                                </div>
                              </div>
                            </Link>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* Content sections - Hover Tabs */}
        {(content?.applications || content?.process || content?.packaging || content?.qualityControlOverview) && (() => {
          type Section = { key: string; icon: any; title: string; render: () => JSX.Element };
          const sections: Section[] = [];

          if (content?.applications) {
            sections.push({
              key: 'applications',
              icon: CheckCircle2,
              title: typeof content.applications.title === 'object'
                ? (content.applications.title[language] || content.applications.title.en)
                : content.applications.title || t('Εφαρμογές', 'Applications'),
              render: () => (
                <div>
                  {content.applications.description && (
                    <p className="text-muted-foreground text-sm mb-3">
                      {typeof content.applications.description === 'object'
                        ? (content.applications.description[language] || content.applications.description.en)
                        : content.applications.description}
                    </p>
                  )}
                  <ul className="space-y-1.5">
                    {(content.applications.items || content.applications[language === 'el' ? 'items' : 'en_items'] || []).map((item: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0"></span>
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            });
          }

          if (content?.process) {
            sections.push({
              key: 'process',
              icon: Factory,
              title: typeof content.process.title === 'object'
                ? (content.process.title[language] || content.process.title.en)
                : content.process.title || t('Εξοπλισμός Επεξεργασίας', 'Process Equipment'),
              render: () => {
                const items: { name: string; description: string; functions?: string[] }[] = [];
                if (Array.isArray(content.process.sections)) {
                  content.process.sections.forEach((section: any) => {
                    const name = typeof section.name === 'object' ? (section.name[language] || section.name.en) : section.name;
                    const desc = section.description || section.en_description;
                    const description = typeof desc === 'object' ? (desc[language] || desc.en) : desc;
                    const functions = language === 'en' && section.en_functions ? section.en_functions : section.functions;
                    items.push({ name, description, functions });
                  });
                } else if (Array.isArray(content.process)) {
                  content.process.forEach((item: any) => {
                    const name = item.name?.[language] || item.name || item.category?.[language] || item.category;
                    const description = item.application?.[language] || item.application || item.features?.[language] || item.features || item.description?.[language] || item.description;
                    items.push({ name, description });
                  });
                }
                return (
                  <div>
                    {slug === 'bakery-grains-snacks' && (
                      <div className="mb-4 rounded-lg overflow-hidden border border-border">
                        <img
                          src={bakeryProcessFlow}
                          alt={t('Διάγραμμα ροής παραγωγής αρτοποιίας', 'Bakery Production Process Flow')}
                          className="w-full h-auto"
                        />
                      </div>
                    )}
                    {items.length === 0 ? (
                      <p className="text-muted-foreground text-sm">{content.process.description}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b">
                            <th className="text-left py-2">{t('Εξοπλισμός', 'Equipment')}</th>
                            <th className="text-left py-2">{t('Περιγραφή', 'Description')}</th>
                            <th className="text-left py-2">{t('Λειτουργίες', 'Functions')}</th>
                          </tr></thead>
                          <tbody>
                            {items.map((item, idx) => (
                              <tr key={idx} className="border-b align-top">
                                <td className="py-2 pr-3 font-medium">{item.name}</td>
                                <td className="py-2 pr-3 text-muted-foreground">{item.description}</td>
                                <td className="py-2 text-muted-foreground">
                                  {Array.isArray(item.functions) ? (
                                    <ul className="space-y-1">
                                      {item.functions.map((func: string, fIdx: number) => (
                                        <li key={fIdx} className="flex items-center gap-2">
                                          <span className="w-1.5 h-1.5 bg-primary/50 rounded-full flex-shrink-0"></span>
                                          {func}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              },
            });
          }

          if (content?.packaging) {
            sections.push({
              key: 'packaging',
              icon: Package,
              title: t('Συσκευασία', 'Packaging'),
              render: () => (
                Array.isArray(content.packaging) ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b">
                        <th className="text-left py-2">{t('Προϊόν', 'Product')}</th>
                        <th className="text-left py-2">{t('Τύπος', 'Type')}</th>
                        <th className="text-left py-2">{t('Δυναμικότητα', 'Capacity')}</th>
                      </tr></thead>
                      <tbody>
                        {content.packaging.map((item: any, idx: number) => {
                          const product = item.product?.[language] || item.product;
                          const type = item.type?.[language] || item.type;
                          const capacity = item.capacity?.[language] || item.capacity;
                          return (
                            <tr key={idx} className="border-b">
                              <td className="py-2">{product}</td>
                              <td className="py-2">{type}</td>
                              <td className="py-2">{capacity}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <></>
              ),
            });
          }

          if (content?.qualityControlOverview) {
            sections.push({
              key: 'qc',
              icon: FlaskConical,
              title: t('Ποιοτικός Έλεγχος', 'Quality Control'),
              render: () => (
                Array.isArray(content.qualityControlOverview) ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b">
                        <th className="text-left py-2">{t('Κατηγορία', 'Category')}</th>
                        <th className="text-left py-2">{t('Εφαρμογή', 'Application')}</th>
                        <th className="text-left py-2">{t('Περιγραφή', 'Description')}</th>
                      </tr></thead>
                      <tbody>
                        {content.qualityControlOverview.map((item: any, idx: number) => {
                          const category = item.category?.[language] || item.category;
                          const application = item.application?.[language] || item.application;
                          const description = item.description?.[language] || item.description;
                          return (
                            <tr key={idx} className="border-b">
                              <td className="py-2">{category}</td>
                              <td className="py-2">{application}</td>
                              <td className="py-2">{description}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <></>
              ),
            });
          }

          const active = sections[Math.min(activeMainTab, sections.length - 1)];

          return (
            <section className="py-10 bg-background">
              <div className="max-w-5xl mx-auto px-4">
                <Card>
                  <CardContent className="p-6">
                    <div
                      className="grid gap-3 mb-6 mx-auto"
                      style={{
                        gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))`,
                        maxWidth: `${sections.length * 240}px`,
                      }}
                    >
                      {sections.map((s, idx) => {
                        const Icon = s.icon;
                        const isActive = idx === activeMainTab;
                        return (
                          <button
                            key={s.key}
                            type="button"
                            onMouseEnter={() => setActiveMainTab(idx)}
                            onFocus={() => setActiveMainTab(idx)}
                            onClick={() => setActiveMainTab(idx)}
                            className={`flex flex-col items-center justify-center gap-3 p-5 min-h-[160px] rounded-lg border-2 transition-all text-center ${
                              isActive
                                ? 'border-primary bg-primary text-primary-foreground shadow-md'
                                : 'border-border bg-muted hover:border-primary/50'
                            }`}
                          >
                            <Icon className="h-9 w-9 flex-shrink-0" />
                            <span className="text-base font-semibold leading-tight">{s.title}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="p-5 bg-muted rounded-lg border-l-4 border-primary animate-fade-in">
                      <h2 className="text-xl font-bold mb-3">{active.title}</h2>
                      {active.render()}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>
          );
        })()}

        {/* Suppliers for this industry */}
        {industryPartners.length > 0 && (
          <section className="py-12 bg-muted">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2">{t('Οι Συνεργάτες μας στον Κλάδο', 'Our Partners in this Industry')}</h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  {t(
                    'Τι προσφέρει κάθε προμηθευτής που εκπροσωπούμε για αυτόν τον κλάδο.',
                    'What each supplier we represent offers for this industry.'
                  )}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {industryPartners.map((partner) => (
                  <Card key={partner.name} className="h-full flex flex-col group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-5 flex flex-col h-full">
                      <SupplierLogo src={partner.logo} alt={`${partner.name} logo`} fallbackText={partner.name} />
                      <div className="flex items-start justify-between gap-2 mt-4 mb-2">
                        <h3 className="text-lg font-semibold group-hover:text-primary transition-colors">{partner.name}</h3>
                        <Badge variant="secondary" className="shrink-0 text-xs">{partner.category[language]}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-2">{partner.description[language]}</p>
                      {partner.strengths && (
                        <p className="text-xs text-muted-foreground/80 italic mb-3">{partner.strengths[language]}</p>
                      )}
                      <div className="flex-grow" />
                      <div className="pt-3 border-t flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-col gap-1">
                          {partner.brochures?.map((b) => (
                            <a key={b.url} href={b.url} className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                              <FileText className="h-3.5 w-3.5" />
                              {b.label[language]}
                            </a>
                          ))}
                        </div>
                        {partner.website && (
                          <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                            {t('Ιστοσελίδα', 'Website')} →
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="text-center mt-8">
                <Button asChild variant="outline">
                  <Link to="/partners">{t('Όλοι οι Συνεργάτες', 'All Partners')} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default IndustryDetail;
